import { useCallback, useRef } from 'react'

import { fetchCityPlanMissionsPage } from 'Module/graphQL'
import { readGraphQLData } from 'Components/PowerBanks/hooks'
import { buildMissionsQueryArgs } from 'Utils/CityPlan'

/**
 * The `fetchData` a DataFetchingComponent-based table needs, bound to one
 * city plan (and optionally one fixed ISO week for the roster page).
 *
 * `toRows` turns the API rows into whatever the table renders; the raw
 * missions of the last page are kept in `missionsById` so row actions can
 * reach them without the table carrying them around.
 *
 * @param {object} args
 * @param {string|number} args.cityPlanId
 * @param {?string} [args.isoWeek]
 * @param {function} args.toRows (missions, {totalCount}) => {data, actions?, footer?}
 * @param {function} [args.onPage] Called with {missions, totalCount, filter, args}
 * @param {function} [args.onError] Called when a page could not be loaded
 * @returns {{fetchData: function, missionsById: {current: Object}}}
 */
export const useCityPlanMissions = ({
  cityPlanId,
  isoWeek,
  toRows,
  onPage,
  onError,
}) => {
  const missionsById = useRef({})

  const fetchData = useCallback(
    async tableArgs => {
      const queryArgs = buildMissionsQueryArgs(tableArgs, { isoWeek })
      let data
      try {
        data = await readGraphQLData(
          await fetchCityPlanMissionsPage({ cityPlanId, ...queryArgs }),
        )
      } catch (error) {
        // DataFetchingComponent has no error path: a rejection would leave
        // the table on its spinner for good. Show an empty page instead.
        // eslint-disable-next-line no-console
        console.error('Could not load the missions page', error)
        if (onError) onError(error)
        return { totalCount: 0, ...toRows([], { totalCount: 0 }) }
      }
      const node = data.cityPlans.edges[0] && data.cityPlans.edges[0].node
      const page = node ? node.missionsPage : { nodes: [], totalCount: 0 }
      const missions = page.nodes || []

      missionsById.current = missions.reduce((acc, mission) => {
        acc[String(mission.id)] = mission
        return acc
      }, {})

      if (onPage)
        onPage({
          missions,
          totalCount: page.totalCount,
          filter: queryArgs.filter,
          orderBy: queryArgs.orderBy,
          args: tableArgs,
        })

      const rows = toRows(missions, { totalCount: page.totalCount })
      return { totalCount: page.totalCount, ...rows }
    },
    [cityPlanId, isoWeek, toRows, onPage, onError],
  )

  return { fetchData, missionsById }
}
